import { useCallback, useEffect, useRef, useState } from 'react';
import { IoChatbubbleEllipses } from 'react-icons/io5';
import { PiTrash } from 'react-icons/pi';

import { getServiceId } from '@/api/tokens.helper';
import { SidePanel, SidePanelHeader } from '@/components';
import { usePerson } from '@/store/persons/person.store';

import {
  AIFetchError,
  chatEquipment,
  deleteEquipmentChatHistory,
  getEquipmentChatHistory,
  streamEquipmentChat,
} from './equipment-ai-chat.api';
import type { ChatMessage, ChatMessageError, ProviderCard } from './equipment-ai-chat.types';
import s from './EquipmentAIChat.module.scss';
import EquipmentAIChatComposer from './EquipmentAIChatComposer';
import EquipmentAIChatMessages from './EquipmentAIChatMessages';

const makeId = () => crypto.randomUUID();

function errorForStatus(err: AIFetchError): ChatMessageError {
  if (err.status === 403) {
    return {
      code: err.code,
      message: 'You don’t have access to this vessel. Please ask its owner for access.',
      retryable: false,
    };
  }
  if (err.status === 502 && err.code === 'upstream_unavailable') {
    return {
      code: err.code,
      message: 'Couldn’t load the equipment data. Please try again later.',
      retryable: false,
    };
  }
  return { code: err.code, message: err.message, retryable: err.retryable };
}

const EquipmentAIChat = () => {
  const { currentUser, me } = usePerson();
  const vesselId = getServiceId();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const isStreaming = messages.some((m) => m.streaming === true);

  const userId = currentUser?.id ?? '';
  const missingIds = !vesselId || !userId;
  const chatDisabled = missingIds || accessDenied;
  const chipDisabled = chatDisabled || isStreaming;

  useEffect(() => {
    void me();
  }, [me]);

  useEffect(() => {
    if (!vesselId || !userId) return;
    getEquipmentChatHistory(vesselId, userId)
      .then((history) => {
        if (history.messages.length > 0) {
          setMessages(
            history.messages.map((m) => ({
              id: makeId(),
              role: m.role,
              content: m.content,
            })),
          );
        }
        setHistoryLoaded(true);
      })
      .catch(() => {
        // Empty or failed history is normal — show the intro, no toast.
        setHistoryLoaded(true);
      });
  }, [vesselId, userId]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const updateMessage = useCallback((id: string, updater: (m: ChatMessage) => ChatMessage) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? updater(m) : m)));
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      if (isStreaming || chatDisabled) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const { signal } = controller;

      const assistantId = makeId();

      setMessages((prev) => [
        ...prev,
        { id: makeId(), role: 'user', content: text },
        {
          id: assistantId,
          role: 'assistant',
          content: '',
          streaming: true,
          retryPayload: text,
        },
      ]);

      let streamSettled = false;

      try {
        await streamEquipmentChat(
          vesselId,
          userId,
          text,
          (eventName, data) => {
            if (signal.aborted) return;

            if (eventName === 'delta') {
              const chunk = (data.text as string | undefined) ?? '';
              updateMessage(assistantId, (m) => ({ ...m, content: m.content + chunk }));
            } else if (eventName === 'status') {
              if (data.stage === 'searching_providers') {
                updateMessage(assistantId, (m) => ({ ...m, statusLabel: 'Searching providers…' }));
              }
            } else if (eventName === 'providers') {
              const providers = (data.providers as ProviderCard[] | undefined) ?? [];
              updateMessage(assistantId, (m) => ({ ...m, providers, statusLabel: undefined }));
            } else if (eventName === 'done') {
              const reply = (data.reply as string | undefined) ?? '';
              const providers = (data.providers as ProviderCard[] | undefined) ?? [];
              updateMessage(assistantId, (m) => ({
                ...m,
                content: reply,
                providers: providers.length > 0 ? providers : m.providers,
                streaming: false,
                statusLabel: undefined,
              }));
              streamSettled = true;
            } else if (eventName === 'error') {
              const errMsg =
                (data.message as string | undefined) ?? 'Couldn’t get a reply. Please try again.';
              const errCode = data.code as string | undefined;
              updateMessage(assistantId, (m) => ({
                ...m,
                streaming: false,
                statusLabel: undefined,
                error: { code: errCode, message: errMsg, retryable: true },
              }));
              streamSettled = true;
            }
          },
          signal,
        );

        // Stream ended without done/error (e.g. keep-alive timeout)
        if (!streamSettled) {
          updateMessage(assistantId, (m) => ({ ...m, streaming: false }));
        }
      } catch {
        if (signal.aborted) return;

        // Fall back once to the non-streaming endpoint
        try {
          const result = await chatEquipment(vesselId, userId, text, signal);
          if (signal.aborted) return;
          updateMessage(assistantId, (m) => ({
            ...m,
            content: result.reply,
            providers: result.providers.length > 0 ? result.providers : undefined,
            streaming: false,
            statusLabel: undefined,
          }));
        } catch (fallbackErr) {
          if (signal.aborted) return;

          if (fallbackErr instanceof AIFetchError) {
            if (fallbackErr.status === 403) setAccessDenied(true);
            if (fallbackErr.status === 422) setInputValue(text);
            const chatErr = errorForStatus(fallbackErr);
            updateMessage(assistantId, (m) => ({
              ...m,
              streaming: false,
              statusLabel: undefined,
              error: chatErr,
            }));
          } else {
            updateMessage(assistantId, (m) => ({
              ...m,
              streaming: false,
              statusLabel: undefined,
              error: { message: 'Couldn’t get a reply. Please try again.', retryable: true },
            }));
          }
        }
      }
    },
    [isStreaming, chatDisabled, vesselId, userId, updateMessage],
  );

  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text) return;
    setInputValue('');
    void sendMessage(text);
  }, [inputValue, sendMessage]);

  const handleRetry = useCallback(
    (messageId: string, originalText: string) => {
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      void sendMessage(originalText);
    },
    [sendMessage],
  );

  const handleClear = useCallback(async () => {
    abortRef.current?.abort();
    try {
      await deleteEquipmentChatHistory(vesselId, userId);
    } catch {
      // best-effort; clear locally regardless
    }
    setMessages([]);
    setAccessDenied(false);
  }, [vesselId, userId]);

  const chatContent = (
    <>
      <div className={s.chatHeader}>
        <SidePanelHeader
          title="Equipment Assistant"
          icon={<IoChatbubbleEllipses />}
          onClose={() => setIsOpen(false)}
          trailing={
            !chatDisabled &&
            messages.length > 0 && (
              <button
                className={s.clearButton}
                onClick={() => void handleClear()}
                type="button"
                title="Clear Chat History"
              >
                <PiTrash />
                <span>Clear</span>
              </button>
            )
          }
        />
      </div>

      <div className={s.messagesArea}>
        <EquipmentAIChatMessages
          messages={messages}
          onRetry={handleRetry}
          historySettled={historyLoaded}
          accessDenied={accessDenied}
          chipDisabled={chipDisabled}
          onSuggestionSelect={(text) => void sendMessage(text)}
        />
      </div>

      {missingIds && !accessDenied && <div className={s.disabledNotice}>Loading vessel data…</div>}
      {accessDenied && (
        <div className={s.disabledNotice}>
          You do not have access to this vessel&apos;s assistant.
        </div>
      )}

      {!accessDenied && (
        <EquipmentAIChatComposer
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSend}
          disabled={chatDisabled}
          userId={userId}
          vesselId={vesselId}
          streaming={isStreaming}
        />
      )}
    </>
  );

  return (
    <>
      {!isOpen && (
        <button
          className={s.launcher}
          onClick={() => setIsOpen(true)}
          type="button"
          aria-label="Open Equipment Assistant"
        >
          <IoChatbubbleEllipses />
        </button>
      )}
      <SidePanel open={isOpen} onClose={() => setIsOpen(false)}>
        {isOpen && chatContent}
      </SidePanel>
    </>
  );
};

export default EquipmentAIChat;
